import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MessageCircle, Send, Loader2 } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { subscribeDirectMessages, sendDirectMessage } from '../../services/directChatService';

function formatMessageTime(ts, locale) {
  if (!ts?.toDate) return '';
  try {
    return ts.toDate().toLocaleString(locale === 'fr' ? 'fr-CA' : 'en-CA', {
      dateStyle: 'short',
      timeStyle: 'short',
    });
  } catch {
    return '';
  }
}

/**
 * @param {{
 *   conversationId: string,
 *   currentUser: { uid: string, displayName?: string, email?: string } | null,
 *   subtitle?: string,
 *   className?: string,
 *   flexibleHeight?: boolean,
 *   hideTitleBar?: boolean,
 * }} props
 */
export default function DirectMessagePanel({
  conversationId,
  currentUser,
  subtitle,
  className = '',
  flexibleHeight = false,
  hideTitleBar = false,
}) {
  const { t, language } = useLanguage();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (!conversationId) return undefined;
    setLoadError(null);
    const unsub = subscribeDirectMessages(
      conversationId,
      (list) => setMessages(list),
      () => setLoadError('load'),
    );
    return () => unsub();
  }, [conversationId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  const displayName = useCallback(() => {
    if (!currentUser) return '';
    return (
      currentUser.displayName?.trim()
      || currentUser.email?.split('@')[0]
      || currentUser.uid?.slice(0, 8)
      || ''
    );
  }, [currentUser]);

  const handleSend = async (e) => {
    e?.preventDefault();
    if (!currentUser?.uid || !conversationId || sending) return;
    const text = input.trim();
    if (!text) return;
    setSending(true);
    try {
      await sendDirectMessage(conversationId, currentUser.uid, displayName(), text);
      setInput('');
    } catch (err) {
      console.warn('DirectMessagePanel send', err);
    } finally {
      setSending(false);
    }
  };

  const onComposerKeyDown = (e) => {
    if (e.key !== 'Enter') return;
    if (!(e.ctrlKey || e.metaKey)) return;
    e.preventDefault();
    handleSend(e);
  };

  if (!conversationId) return null;

  return (
    <div
      className={`flex flex-col border border-slate-200 rounded-xl bg-white shadow-sm overflow-hidden min-h-0 ${className}`}
    >
      {!hideTitleBar ? (
        <div className="px-3 py-2 border-b border-slate-100 bg-slate-50 flex items-center gap-2">
          <MessageCircle className="w-4 h-4 text-indigo-600 shrink-0" aria-hidden />
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-slate-800 truncate">{t('directChat.title')}</h3>
            {subtitle ? <p className="text-xs text-slate-500 truncate">{subtitle}</p> : null}
          </div>
        </div>
      ) : null}
      <p className="text-[11px] text-amber-800 bg-amber-50 px-3 py-1.5 border-b border-amber-100">
        {t('directChat.sensitiveHint')}
      </p>
      {loadError ? (
        <p className="text-sm text-red-600 px-3 py-4">{t('directChat.loadError')}</p>
      ) : (
        <div
          className={`overflow-y-auto px-3 py-2 space-y-2 bg-slate-50/50 ${
            flexibleHeight ? 'flex-1 min-h-[120px]' : 'h-56'
          }`}
        >
          {messages.length === 0 ? (
            <p className="text-sm text-slate-500 py-4 text-center">{t('directChat.empty')}</p>
          ) : (
            messages.map((m) => {
              const mine = m.authorUid === currentUser?.uid;
              const label = mine ? t('directChat.you') : (m.authorDisplayName || m.authorUid || '?');
              return (
                <div
                  key={m.id}
                  className={`rounded-lg px-2.5 py-1.5 text-sm max-w-[95%] ${
                    mine ? 'ml-auto bg-indigo-600 text-white' : 'mr-auto bg-white border border-slate-200 text-slate-800'
                  }`}
                >
                  <div className={`text-[10px] font-medium mb-0.5 ${mine ? 'text-indigo-100' : 'text-slate-500'}`}>
                    {label}
                    {m.createdAt ? (
                      <span className="font-normal opacity-80 ml-1">
                        · {formatMessageTime(m.createdAt, language)}
                      </span>
                    ) : null}
                  </div>
                  <div className="whitespace-pre-wrap break-words">{m.text}</div>
                </div>
              );
            })
          )}
          <div ref={bottomRef} />
        </div>
      )}
      {currentUser ? (
        <form onSubmit={handleSend} className="p-2 border-t border-slate-100 flex gap-2 items-end bg-white">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onComposerKeyDown}
            placeholder={t('directChat.placeholder')}
            rows={2}
            maxLength={8000}
            className="flex-1 text-sm border border-slate-200 rounded-lg px-2 py-1.5 resize-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500"
            disabled={sending || !!loadError}
          />
          <button
            type="submit"
            disabled={sending || !input.trim() || !!loadError}
            className="shrink-0 p-2 rounded-lg bg-indigo-600 text-white disabled:opacity-40 hover:bg-indigo-700"
            aria-label={t('directChat.send')}
            title={t('directChat.sendShortcutHint')}
          >
            {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
          </button>
        </form>
      ) : (
        <p className="text-sm text-slate-600 px-3 py-3 border-t border-slate-100">{t('directChat.loginToParticipate')}</p>
      )}
    </div>
  );
}
